import React, { useState } from 'react';
import { Alert, FormField, Input, SpaceBetween, TokenGroup } from '@amzn/awsui-components-react-v3';

export interface AutoOnboardFilterInputProps {
  includePatterns: string[];
  excludePatterns: string[];
  onIncludePatternsChange: (patterns: string[]) => void;
  onExcludePatternsChange: (patterns: string[]) => void;
  disabled?: boolean;
}

const PATTERN_REGEX = /^[a-zA-Z0-9_*?\-\.]+$/;

const isValidPattern = (pattern: string): boolean => {
  return pattern.length > 0 && pattern.length <= 255 && PATTERN_REGEX.test(pattern);
};

export const AutoOnboardFilterInput = ({
  includePatterns,
  excludePatterns,
  onIncludePatternsChange,
  onExcludePatternsChange,
  disabled = false,
}: AutoOnboardFilterInputProps) => {
  const [includeInput, setIncludeInput] = useState('');
  const [excludeInput, setExcludeInput] = useState('');

  const addPattern = (
    input: string,
    patterns: string[],
    setInput: (v: string) => void,
    onChange: (p: string[]) => void,
  ) => {
    const trimmed = input.trim();
    if (isValidPattern(trimmed) && !patterns.includes(trimmed) && patterns.length < 50) {
      onChange([...patterns, trimmed]);
      setInput('');
    } else if (trimmed === '') {
      setInput('');
    }
  };

  const handleChange = (
    value: string,
    patterns: string[],
    setInput: (v: string) => void,
    onChange: (p: string[]) => void,
  ) => {
    if (value.includes(' ') || value.includes(',')) {
      const cleaned = value.replace(/[,\s]/g, '');
      addPattern(cleaned, patterns, setInput, onChange);
    } else {
      setInput(value);
    }
  };

  const removePattern = (index: number, patterns: string[], onChange: (p: string[]) => void) => {
    onChange([...patterns.slice(0, index), ...patterns.slice(index + 1)]);
  };

  return (
    <SpaceBetween size='s'>
      <FormField
        label='Include patterns - optional'
        description='Use * for any characters, ? for single character (e.g., prod_*, *_final). Only tables matching these patterns will be onboarded.'
      >
        <Input
          value={includeInput}
          onChange={({ detail }) =>
            handleChange(detail.value, includePatterns, setIncludeInput, onIncludePatternsChange)
          }
          onKeyDown={({ detail }) => {
            if (detail.key === 'Enter') {
              addPattern(includeInput, includePatterns, setIncludeInput, onIncludePatternsChange);
            }
          }}
          onBlur={() => addPattern(includeInput, includePatterns, setIncludeInput, onIncludePatternsChange)}
          placeholder='prod_*'
          disabled={disabled}
        />
        <TokenGroup
          items={includePatterns.map((p) => ({ label: p }))}
          onDismiss={({ detail: { itemIndex } }) => removePattern(itemIndex, includePatterns, onIncludePatternsChange)}
        />
      </FormField>

      <FormField
        label='Exclude patterns - optional'
        description='Tables matching these patterns will not be onboarded (e.g., *_staging, temp*).'
      >
        <Input
          value={excludeInput}
          onChange={({ detail }) =>
            handleChange(detail.value, excludePatterns, setExcludeInput, onExcludePatternsChange)
          }
          onKeyDown={({ detail }) => {
            if (detail.key === 'Enter') {
              addPattern(excludeInput, excludePatterns, setExcludeInput, onExcludePatternsChange);
            }
          }}
          onBlur={() => addPattern(excludeInput, excludePatterns, setExcludeInput, onExcludePatternsChange)}
          placeholder='*_staging'
          disabled={disabled}
        />
        <TokenGroup
          items={excludePatterns.map((p) => ({ label: p }))}
          onDismiss={({ detail: { itemIndex } }) => removePattern(itemIndex, excludePatterns, onExcludePatternsChange)}
        />
      </FormField>

      <Alert type='info'>Filter patterns only apply to new tables. Existing registrations are not affected.</Alert>
    </SpaceBetween>
  );
};
